import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import StickyBox from 'react-sticky-box';
import { useDispatch, useSelector } from 'react-redux';
import { useGetSubscriptionsQuery } from '@features/WordPress/services/wordpress';
import { useRouter } from 'next/router';
import { mapLocaleCodeToCountry } from '@features/WordPress/utilities/helpers/locale';
import {
  useCreateMembershipMutation,
  useGetSummariesMutation,
  useUpdateMembershipMutation,
} from '@features/Checkout/services/checkout';

import {
  ContentWrapper,
  CheckoutStep,
} from '@features/Checkout/components/CheckoutSteps/CheckoutSteps.module.css';
import {
  setFormIsValid,
  setIsValidating,
  setPlanSelectedFromQuery,
} from '@features/Checkout/store';
import Button from '@components/Button';
import useProvideLocations from '@hooks/use-provide-locations';
import CheckoutSidebar from '@features/Checkout/components/CheckoutSidebar';
import LoadingIcon from '@components/LoadingIcon';

import useAuth from '@features/MyAccount/hooks/use-auth';
import { useErrorHandler } from 'react-error-boundary';
import { captureException } from '@sentry/nextjs';
import { useTranslation } from 'next-i18next';
import {
  calculateStartDate,
  formatLocation,
} from '@features/Checkout/utilities/checkout';
import { isEmpty } from 'lodash';
import { useGetMeQuery } from '@shared/services/users';
import { pushViewItemToDataLayer } from '@utilities/helpers/common';
import {
  AddCarButton,
  StickyBox as StickyBoxClass,
} from './Membership.module.css';

import MembershipBuilder from './components/MembershipBuilder';
import { Plans } from '@shared/types/plans';

const Membership = ({ onStepCompleted }) => {
  const { t } = useTranslation(['account', 'common', 'checkout']);
  const dispatch = useDispatch();
  const { isAuthenticated: isAuth } = useAuth(false);
  const { data: user } = useGetMeQuery(null, { skip: !isAuth });
  const { locale, query, replace, isReady } = useRouter();
  const {
    membershipIds = [],
    couponCode,
    isSingle,
    isBusiness,
    isRemovingMembership,
    terminalSession,
    agreement,
  } = useSelector((state) => state.checkout);
  const locations = useProvideLocations();
  const submitCountRef = useRef(0);
  const handleError = useErrorHandler();
  const prefill = [
    'lpn',
    'location',
    'plan',
    'single',
    'atav',
    'p',
    'c',
    's',
    'a',
  ];
  const hasPrefillInQuery =
    prefill.filter((str) => Object.keys(query).some((key) => key === str))
      .length > 0;

  const { options } = useSelector((state) => state.wordpress);

  const [isUpdatingMembershipsFromQuery, setIsUpdatingMembershipsFromQuery] =
    useState(false);
  const [membershipStepComplete, setMembershipStepComplete] = useState(false);

  const [
    createMembership,
    { isLoading: isCreatingMembership, data: createdMembership },
  ] = useCreateMembershipMutation();

  const [
    updateMembership,
    { isLoading: isUpdatingMembership, data: updatedMembership },
  ] = useUpdateMembershipMutation();

  const [
    getSummaries,
    { data: membershipsSummary, isLoading: isFetchingSummary },
  ] = useGetSummariesMutation({
    fixedCacheKey: 'summary-shared',
  });

  useEffect(() => {
    if (!membershipIds.length || isUpdatingMembership || isCreatingMembership)
      return;

    const summary = getSummaries({
      ids: membershipIds,
      coupons: [couponCode],
      vat: isBusiness,
      terminalSession,
    });

    summary.unwrap().catch((err) => {
      if (err.name === 'AbortError') return;
      // eslint-disable-next-line no-console
      console.error(err);
    });

    return () => {
      summary.abort();
    };
  }, [
    createdMembership,
    updatedMembership,
    isRemovingMembership,
    getSummaries,
    membershipIds,
    couponCode,
    isBusiness,
    terminalSession,
    isUpdatingMembership,
    isCreatingMembership,
  ]);

  // Data layers
  useEffect(() => {
    if (!membershipsSummary) return;

    pushViewItemToDataLayer(
      membershipsSummary.summary,
      membershipsSummary.total,
      options?.currencyCode,
      couponCode
    );
  }, [membershipsSummary, couponCode, options?.currencyCode]);

  // Get location from query
  const locationFromQuery = useCallback(
    () =>
      (query?.location &&
        locations.length > 0 &&
        locations.find(({ uid }) => uid === query?.location)) ||
      {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locations, query]
  );

  // Get membership start date
  const membershipStartDateFromQuery = useCallback(
    (uid, { grand_opening }) => {
      const membership =
        uid &&
        membershipsSummary?.summary.find(({ uid: match }) => uid === match);
      const startDate = calculateStartDate(grand_opening);
      return {
        membership,
        startDate,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locations]
  );
  const mapMembershipToFormData = useCallback((object) => {
    const {
      uid,
      location,
      location_name,
      location_grand_opening,
      location_soft_opening,
      machine_type,
      plate,
      plans,
    } = object;

    const startDate = null;

    return {
      uid,
      plan: plans.find(({ is_addon }) => !is_addon)?.plan,
      atav: !!plans.find(({ is_addon }) => is_addon)?.uid,
      startDate,
      licensePlate: plate,
      primaryLocation: location
        ? formatLocation({
            uid: location,
            name: location_name,
            grand_opening: location_grand_opening,
            soft_opening: location_soft_opening,
            machine_type,
          })
        : false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Fetch subscriptions
   */

  const { data: subscriptions = [], isLoading: isFetchingPlans } =
    useGetSubscriptionsQuery({
      country: mapLocaleCodeToCountry(locale),
      business: isBusiness,
    });

  const {
    handleSubmit,
    control,
    register,
    setValue,
    getValues,
    setError,
    clearErrors,
    watch,
    formState: { errors, isValidating, submitCount },
    trigger,
  } = useForm({
    mode: 'onSubmit',
    shouldUnregister: false,
    reValidateMode: 'onBlur',
    defaultValues: {
      memberships: [],
    },
    criteriaMode: 'all',
  });

  const formMemberships = watch('memberships', []);
  const onlyFourOrLessCars = formMemberships.length <= 3;

  //  // useful for debugging:
  // const setValue = (...params) => {
  //   console.trace('calling set value on', params[0], params[1]);
  //   setValueFn(...params);
  // };

  /**
   * Check if form is valid
   */
  useEffect(() => {
    dispatch(setFormIsValid(Object.keys(errors).length === 0));
    dispatch(setIsValidating(isValidating));
  }, [errors, isValidating, dispatch, updateMembership, membershipsSummary]);

  /**
   * Scroll primary location into view if field is missing
   */
  useEffect(() => {
    const missingFirstPrimaryLocation =
      errors?.memberships?.[0]?.primaryLocation;
    // scroll into view if missing location
    if (missingFirstPrimaryLocation && submitCount !== submitCountRef.current) {
      submitCountRef.current = submitCount;
      document
        .querySelector(`input[name="${missingFirstPrimaryLocation?.ref.name}"]`)
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
    }
  }, [submitCount, errors]);

  /**
   * This method will be called when the form has been submitted and has
   * passed validation. Feel free to add any extra validation you may want
   * in this callback. Simply end by calling onStepCompleted();
   */
  const onSubmit = useCallback(
    async (data) => {
      setMembershipStepComplete(true);
      onStepCompleted(data);
    },
    [onStepCompleted]
  );

  /**
   * Ensure we always have a default plan
   */
  useEffect(() => {
    if (!isReady) return;
    if (
      subscriptions.length &&
      !membershipIds?.length &&
      !isCreatingMembership &&
      locations.length &&
      !isUpdatingMembershipsFromQuery
    ) {
      const newMembership = {
        // if single wash
        ...(Boolean(Number(query?.single)) && { loyalty: true }),

        // Empty license plate to ensure request errors
        licensePlate: query?.lpn || null,

        // If agreement in query prefill membership with it
        membershipAgreement: query?.a || null,

        // Assign location from query param, or the first on the list.
        // Start date today or if location from query is future date
        ...(query?.location &&
          !isEmpty(locationFromQuery()) && {
            primaryLocation: locationFromQuery(),
            ...(!terminalSession && {
              startDate: membershipStartDateFromQuery(null, locationFromQuery())
                .startDate,
            }),
          }),

        // Assign plan from query parameter, otherwise a popular one, lastly the first from list
        /* ...(!Number(query?.single) && {
          plan: query?.plan || subscriptions.find(
            ({ is_popular: isPopular }) => isPopular
          )?.id
        }), */
        ...(Number(query?.single) && {
          loyalty: Boolean(Number(query?.single)),
        }),

        ...(!Number(query?.single) && {
          plan:
            (locale === 'da' &&
              ((query?.plan === Plans.BASIS && Plans.GULD) ||
                (query?.plan === Plans.PREMIUM_PLUS && Plans.ALL_INCLUSIVE))) ||
            query?.plan ||
            subscriptions.find(({ is_popular: isPopular }) => isPopular)?.id,
        }),

        // If atav in query prefill membership with atav
        ...(query?.atav && {
          access_all: Boolean(Number(query?.atav)),
        }),

        // User (if authenticated)
        ...(isAuth && {
          user: user.uid,
        }),
      };

      if (hasPrefillInQuery) {
        setIsUpdatingMembershipsFromQuery(true);
      }

      createMembership(newMembership)
        .unwrap()
        .then(({ validatedParams, ...membership }) => {
          if (validatedParams?.license_plate) {
            setTimeout(
              () =>
                setValue('memberships', [
                  mapMembershipToFormData({ plate: query?.lpn, ...membership }),
                ]),
              150
            );
          } else {
            setTimeout(
              () =>
                setValue('memberships', [mapMembershipToFormData(membership)]),
              150
            );
          }
        })
        .catch(handleError)
        .finally(() => {
          // remove query strings from URL
          if (hasPrefillInQuery) {
            replace(
              {
                pathname: window.location.pathname,
              },
              undefined,
              { shallow: true }
            );
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locations,
    subscriptions,
    membershipIds,
    isCreatingMembership,
    isReady,
    user,
  ]);

  /**
   * Populate form with memberships from API
   */
  useEffect(() => {
    let timeout;

    if (
      membershipsSummary?.summary?.length &&
      !formMemberships?.length &&
      !hasPrefillInQuery &&
      !isUpdatingMembershipsFromQuery
    ) {
      const membershipDataMergedFromApi = membershipsSummary?.summary.map(
        (membership) => mapMembershipToFormData(membership)
      );

      timeout = setTimeout(() => {
        setValue('memberships', membershipDataMergedFromApi);
      }, 150);
    }

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    membershipsSummary,
    formMemberships,
    hasPrefillInQuery,
    isUpdatingMembershipsFromQuery,
  ]);

  /**
   * Update all existing memberships from API; then fetch from above hook
   */
  useEffect(() => {
    if (!isReady) return;
    if (
      !isUpdatingMembershipsFromQuery &&
      hasPrefillInQuery &&
      membershipIds.length &&
      locations.length
    ) {
      // set flag, to prevent updating twice
      setIsUpdatingMembershipsFromQuery(true);

      Promise.all(
        membershipIds.map((uid) =>
          updateMembership({
            // membership
            uid,

            membershipAgreement: agreement,

            // single wash membership
            ...(Number(query?.single) && {
              loyalty: Boolean(Number(query?.single)),
            }),

            /* set plan */
            ...(!Number(query?.single) && {
              plan:
                (locale === 'da' &&
                  ((query?.plan === Plans.BASIS && Plans.GULD) ||
                    (query?.plan === Plans.PREMIUM_PLUS &&
                      Plans.ALL_INCLUSIVE))) ||
                query?.plan ||
                subscriptions.find(({ is_popular: isPopular }) => isPopular)
                  ?.id,
            }),

            // if washback set start date to current date
            ...(terminalSession && {
              startDate: new Date(),
            }),

            // set location and start date
            ...(query?.location && {
              primaryLocation: locations.find(
                ({ uid: locationUid }) => locationUid === query?.location
              ),
              startDate: membershipStartDateFromQuery(
                uid,
                locations.find(
                  ({ uid: locationUid }) => locationUid === query?.location
                )
              )?.startDate,
            }),

            // set licensePlate
            ...(query?.lpn && {
              licensePlate: query?.lpn,
            }),

            // If atav in query prefill membership with atav
            ...(query?.atav && {
              access_all: Boolean(Number(query?.atav)),
            }),

            // user (if authenticated)
            ...(isAuth && {
              user: user.uid,
            }),
          })
            .unwrap()
            .then(({ validatedParams, ...membership }) =>
              validatedParams?.license_plate?.type === 'duplicate'
                ? mapMembershipToFormData({ ...membership, plate: query?.lpn })
                : mapMembershipToFormData(membership)
            )
            .catch(handleError)
        )
      )
        .then((updatedMemberships) =>
          setValue('memberships', updatedMemberships)
        )
        .catch((error) => {
          captureException('error', error);
          throw new Error('Error updating membership');
        })
        .finally(() => {
          // remove query strings from URL
          if (hasPrefillInQuery) {
            replace(
              {
                pathname: window.location.pathname,
              },
              undefined,
              { shallow: true }
            );
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locations,
    membershipIds,
    hasPrefillInQuery,
    isUpdatingMembershipsFromQuery,
    isReady,
  ]);

  if (process.browser) {
    window.getValues = getValues;
  }

  /**
   * When clicking add car, add a new membership and append the uid to the array
   */
  const onClickAddCar = () => {
    if (!onlyFourOrLessCars) return;

    const currentMemberships = formMemberships?.filter(({ uid }) => uid);

    const newMembership = {
      ...(currentMemberships[currentMemberships.length - 1] || {}),
      licensePlate: null,
      uid: null,

      // user (if authenticated)
      ...(isAuth && {
        user: user.uid,
      }),
    };

    createMembership(newMembership)
      .unwrap()
      .then(({ uid }) => {
        setValue('memberships', [
          ...currentMemberships,
          {
            ...newMembership,
            uid,
          },
        ]);
      })
      .catch(handleError);
  };

  // Scroll new membership into view
  useEffect(() => {
    if (
      formMemberships.length <= 1 ||
      isCreatingMembership ||
      isRemovingMembership
    )
      return;
    setTimeout(() => {
      document
        .querySelector(
          `[data-membership-index="${formMemberships.length - 1}"]`
        )
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMemberships]);

  /**
   * Plan selected from query
   */
  useEffect(() => {
    if (query?.plan) {
      dispatch(setPlanSelectedFromQuery(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isFetchingPlans || isUpdatingMembershipsFromQuery || !isReady) {
    return (
      <div>
        <LoadingIcon size="large" withWrapper />
      </div>
    );
  }

  return (
    <form className={ContentWrapper} onSubmit={handleSubmit(onSubmit)}>
      <StickyBox offsetTop={115} offsetBottom={50} className={StickyBoxClass}>
        <CheckoutSidebar
          buttonText={t('next', { ns: 'common' })}
          isLoading={isFetchingSummary}
          animate
          memberships={membershipsSummary?.summary}
          checkoutPrice={membershipsSummary?.total}
          couponCode={couponCode}
          membershipStepComplete={membershipStepComplete}
        />
      </StickyBox>
      <div className={CheckoutStep}>
        <MembershipBuilder
          subscriptions={subscriptions}
          onClickAddCar={onClickAddCar}
          updateMembership={updateMembership}
          isUpdatingMembership={isUpdatingMembership}
          {...{
            control,
            register,
            setValue,
            getValues,
            setError,
            clearErrors,
            trigger,
            errors,
          }}
        />
        {formMemberships.length > 0 && !isSingle && (
          <div>
            {!onlyFourOrLessCars ? (
              <div className={AddCarButton}>
                {t('addMoreThanFourCars', { ns: 'checkout' })}
              </div>
            ) : (
              <Button
                size="large"
                appearance="text"
                className={AddCarButton}
                noIconAnimation
                onClick={onClickAddCar}
                disabled={
                  isCreatingMembership ||
                  isUpdatingMembership ||
                  isFetchingSummary
                }
              >
                {t('addExtraCar', { ns: 'checkout' })}
              </Button>
            )}
          </div>
        )}
      </div>
    </form>
  );
};

export default Membership;
